import { getMetadata } from '../shared/metadata'

// What a course is built as (owner 2026-10-09): the words content's hidden
// `filter` column used to match as free text ("v1", "v2", "plus", "urp")
// are now explicit, like "Available on"'s "web:" -- typed "v2:" in the
// search box, or picked from the "Format" filter beside it -- and no longer
// match in the search text. The tests are content's own gc_filter rules,
// read from the node's metadata:
//   v2   CourseAssemblyName is "caffeine"
//   v1   anything else
//   plus CaffeinePlusEnabled is true
//   urp  RenderPipelineName is "urp"
// Only courses (and templates) have a format; a folder is kept when it holds
// a match, at any depth, so it can still be opened. Videos have none.
export const FORMATS = [
  { key: 'v1', label: 'v1', hover: 'Built before caffeine (v1)', aliases: ['v1'] },
  { key: 'v2', label: 'v2', hover: 'Built with caffeine (v2)', aliases: ['v2'] },
  { key: 'plus', label: 'Plus', hover: 'Caffeine Plus', aliases: ['plus'] },
  { key: 'urp', label: 'URP', hover: 'Universal Render Pipeline', aliases: ['urp'] }
]

const BY_ALIAS = new Map(FORMATS.flatMap(f => f.aliases.map(alias => [alias, f.key])))

export const formatLabel = key => {
  const format = FORMATS.find(f => f.key === key)
  return format ? format.label : ''
}

const metadataOf = node => {
  try {
    return getMetadata(node)
  } catch (e) {
    return {}
  }
}

export const matchesFormat = (node, format) => {
  const md = metadataOf(node)
  switch (format) {
    case 'v2':
      return String(md.courseassemblyname) === 'caffeine'
    case 'v1':
      return String(md.courseassemblyname) !== 'caffeine'
    case 'plus':
      return String(md.caffeineplusenabled) === 'true'
    case 'urp':
      return String(md.renderpipelinename) === 'urp'
    default:
      return true
  }
}

// The search box's shortcut: "v2:" (any case, anywhere in the text, with or
// without a space after it) picks that format; the rest of the text is the
// search. { format: null } when there is none.
// (Whitespace after a word that isn't a format stays put, so "web: v2:"
// still finds "v2:".)
const SHORTCUT = /(^|\s)([a-z0-9]+):/gi

export const takeFormatShortcut = value => {
  const text = value || ''
  SHORTCUT.lastIndex = 0
  let match
  while ((match = SHORTCUT.exec(text))) {
    const format = BY_ALIAS.get(match[2].toLowerCase())
    if (format) {
      const start = match.index + match[1].length
      let end = match.index + match[0].length
      while (end < text.length && /\s/.test(text[end])) end++
      return { format, text: text.slice(0, start) + text.slice(end) }
    }
  }
  return { format: null, text }
}

// The area's rows with a format chosen: its courses of that format (`isCourse`
// says which rows have one) and every row above one of them, in the list's
// order. No format: the rows as they are.
export const keepFormat = (rows, format, isCourse) => {
  if (!format || !Array.isArray(rows)) return rows || []
  const byID = new Map(rows.map(r => [r.id, r]))
  const keep = new Set()
  for (const row of rows) {
    if (!isCourse(row) || !matchesFormat(row, format)) continue
    keep.add(row.id)
    let cursor = row.parentID
    let depth = 0
    while (cursor !== null && cursor !== undefined && !keep.has(cursor) && depth++ < 32) {
      const parent = byID.get(cursor)
      if (!parent) break
      keep.add(cursor)
      cursor = parent.parentID
    }
  }
  return rows.filter(r => keep.has(r.id))
}
